import React, {useState} from 'react';

import {Button, Content, DropDown, InputNumber, Trigger, useDropDownContext} from '@kit';

import {cls} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import {withJournalEntry} from '../model/journal';
import {DeathSaveMarks} from './death-saves';
import {HealDamageForm} from './heal-damage-form';
import {HitDice} from './hit-dice';
import {HitPointsModal} from './hit-points-modal';
import {
	ArmorClassCard,
	DefensesCard,
	InitiativeCard,
	InspirationCard,
	ProficiencyBonusCard,
	SizeCard,
	SpeedCard,
} from './stat-cards';

import css from './sheet.module.css';

/** ряд плашек над вкладками на широком экране */
export const VitalsBlock: React.FC = () => (
	<div className={css.statsContainer}>
		<div className={css.stats}>
			<ArmorClassCard />
			<HealthCard />
			<SpeedCard />
			<InspirationCard />
			<InitiativeCard />
			<DefensesCard />
			<SizeCard />
			<ProficiencyBonusCard />
		</div>
	</div>
);

/** широкая плашка здоровья: нажатие по ПЗ открывает управление */
const HealthCard: React.FC = () => {
	const {character} = useCharacter();
	const [isManaging, setManaging] = useState(false);

	return (
		<section className={css.healthCard}>
			<HealDamageForm vertical />
			<dl className={css.healthStats}>
				<div className={css.healthStat}>
					<dt className={css.statLabel}>ПЗ</dt>
					<dd>
						<button
							type="button"
							className={cls(css.rollButton, css.hitPointsButton)}
							title="Управление ПЗ"
							onClick={() => setManaging(true)}
						>
							{character.currentHitPoints > 0 ? (
								<span className={css.hitPointsCurrent}>
									{character.currentHitPoints}
									<span className={css.hitPointsMax}> / {character.maxHitPoints}</span>
								</span>
							) : (
								<DeathSaveMarks />
							)}
						</button>
					</dd>
				</div>
				<TemporaryHitPoints />
				<HitDice />
			</dl>
			{isManaging && <HitPointsModal onClose={() => setManaging(false)} />}
		</section>
	);
};

const TemporaryHitPoints: React.FC = () => {
	const {character} = useCharacter();
	return (
		<div className={css.healthStat}>
			<dt className={css.statLabel}>Временные ПЗ</dt>
			<dd>
				<DropDown>
					<Trigger>
						<button
							type="button"
							className={cls(css.rollButton, css.hitPointsButton)}
							title="Задать временные ПЗ"
						>
							<span className={css.hitPointsCurrent}>{character.currentTemporaryHitPoints}</span>
						</button>
					</Trigger>
					<Content align="end">
						<TemporaryHitPointsForm />
					</Content>
				</DropDown>
			</dd>
		</div>
	);
};

/** Enter задаёт значение и закрывает выпадашку */
const TemporaryHitPointsForm: React.FC = () => {
	const {update} = useCharacter();
	const drop = useDropDownContext();
	const [value, setValue] = useState<number | null>(null);

	const apply = (event: React.FormEvent) => {
		event.preventDefault();
		if (value == null) return;
		update((current) => ({
			hitPoints: {...current.hitPoints, temporary: Math.max(0, value), temporarySpent: 0},
			...withJournalEntry(current, {kind: 'hitPoints', title: 'Временные ПЗ', total: Math.max(0, value)}),
		}));
		setValue(null);
		drop?.setOpen(false);
	};

	return (
		<form className={css.temporaryPopover} onSubmit={apply}>
			<InputNumber min={0} placeholder="0" value={value} onChange={setValue} autoFocus />
			<Button type="submit" appearance="primary" disabled={value == null}>
				Задать
			</Button>
			<div className={css.muted}>Новое значение заменяет прежнее.</div>
		</form>
	);
};
